// @ts-nocheck
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguageContext } from "../context/languageContext";
import { addServiceRequest } from "../stores/userInfo/detailsSlice";
import { setRoomId } from "../stores/business/businessSlice";
import { API_URL } from "../config/api";

const ServiceRequestPage = () => {
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [description, setDescription] = useState("");
    const [extras] = useState({});
    const [roomServiceId, setRoomServiceId] = useState("");

    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t, language } = useLanguageContext();
    const business = useSelector((state) => state.business);

    const product = location.state?.product;
    const categoryItems = location.state?.categoryItems || [];
    const [locationUnits, setLocationUnits] = useState([]);
    const [loadingUnits, setLoadingUnits] = useState(true);

    const PUBLIC_TOKEN = "eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiJkZW5pcy5taW1pbmlAY2xpY2thbGJhbmlhLmNvbSIsImlhdCI6MTc1MDk4MjU3NywiZXhwIjoxNzUxMjQxNzc3fQ.yQ3xGQEfpj62ruJoLJYOl3966g55OlyofRqjoAHzu";

    useEffect(() => {
        const fetchLocationUnits = async () => {
            try {
                const businessId = business.businessId || '2';
                const response = await fetch(`${API_URL}/api/v1/business/${businessId}/location-units`, {
                    headers: {
                        Authorization: `Bearer ${PUBLIC_TOKEN}`,
                        "Content-Type": "application/json",
                    },
                });
                if (response.ok) {
                    const data = await response.json();
                    const units = Array.isArray(data) ? data : [];
                    setLocationUnits(units);

                    // Pre-populate roomServiceId if business.roomId exists
                    if (business.roomId && units.length > 0) {
                        const match = units.find(u =>
                            String(u.id) === String(business.roomId) ||
                            u.name?.toLowerCase() === String(business.roomId).toLowerCase()
                        );
                        if (match) {
                            setRoomServiceId(String(match.id));
                        } else {
                            // If no direct match but we have a roomId, we can still set it
                            setRoomServiceId(String(business.roomId));
                        }
                    }
                }
            } catch (error) {
                console.error("Error fetching location units:", error);
            } finally {
                setLoadingUnits(false);
            }
        };
        fetchLocationUnits();
    }, [business.businessId, business.roomId]);

    // Helpers: distance & verification (same logic as Order Summary)
    const getDistance = (lat1, lon1, lat2, lon2) => {
        const R = 6371;
        const dLat = ((lat2 - lat1) * Math.PI) / 180;
        const dLon = ((lon2 - lon1) * Math.PI) / 180;
        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos((lat1 * Math.PI) / 180) *
            Math.cos((lat2 * Math.PI) / 180) *
            Math.sin(dLon / 2) ** 2;
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c * 1000;
    };

    const checkLocation = (businessLat, businessLng, maxDistance = 800) =>
        new Promise((resolve) => {
            if (!navigator.geolocation) return resolve(false);
            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    const dist = getDistance(pos.coords.latitude, pos.coords.longitude, businessLat, businessLng);
                    resolve(dist <= maxDistance);
                },
                () => resolve(false),
                { enableHighAccuracy: true, timeout: 10000 }
            );
        });

    const checkIP = async (allowedIP) => {
        try {
            const res = await fetch("https://api.ipify.org?format=json");
            const data = await res.json();
            return data.ip === allowedIP;
        } catch { return false; }
    };

    if (!product) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex items-center justify-center px-4">
                <div className="bg-white rounded-3xl shadow-xl p-8 text-center max-w-md border border-slate-100">
                    <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-bold text-slate-800 mb-2">{t("Service not found")}</h3>
                    <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 transition-colors">
                        {t("Go Back")}
                    </button>
                </div>
            </div>
        );
    }

    const getTranslatedName = (item) => {
        if (!item?.itemTranslation) return item.name;
        const map = {
            en: item.itemTranslation.englishName,
            it: item.itemTranslation.italianName,
            sq: item.itemTranslation.albanianName,
            de: item.itemTranslation.germanName,
            es: item.itemTranslation.spanishName,
            pl: item.itemTranslation.polishName,
            ukr: item.itemTranslation.ukrainianName,
        };
        return map[language] || item.name;
    };

    const getTranslatedDescription = (item) => {
        if (!item?.itemTranslation) return item.description;
        const map = {
            en: item.itemTranslation.englishDescription,
            it: item.itemTranslation.italianDescription,
            sq: item.itemTranslation.albanianDescription,
            de: item.itemTranslation.germanDescription,
            es: item.itemTranslation.spanishDescription,
            pl: item.itemTranslation.polishDescription,
            ukr: item.itemTranslation.ukrainianDescription,
        };
        return map[language] || item.description;
    };



    const calculateTotal = () => {
        let total = product.price;
        Object.keys(extras).forEach((id) => {
            if (extras[id]) {
                const item = categoryItems.find((i) => String(i.id) === String(id));
                if (item) total += item.price;
            }
        });
        return total;
    };

    const onSubmit = async () => {
        // Validation
        if (!roomServiceId.trim()) {
            alert(t("Please enter your room service ID"));
            return;
        }

        // Business verification values (match Order Summary)
        const businessLat = 41.313436;
        const businessLng = 19.475683;
        const businessIP = "46.252.34.88";

        try {
            // Verify user is near the business location OR on allowed IP
            const locationOk = await checkLocation(businessLat, businessLng);
            const ipOk = await checkIP(businessIP);

            if (!locationOk && !ipOk) {
                alert(t("must_be_near"));
                return;
            }

            setSubmitting(true);
            const selectedExtras = Object.keys(extras).filter((id) => extras[id]);
            const selectedUnit = locationUnits.find(u => String(u.id) === String(roomServiceId));
            const formData = {
                serviceItems: [
                    { id: product.id, name: getTranslatedName(product), price: product.price },
                    ...categoryItems
                        .filter((item) => selectedExtras.includes(String(item.id)))
                        .map((extra) => ({ id: extra.id, name: getTranslatedName(extra), price: extra.price })),
                ],
                business: business.businessInfo,
                description,
                serviceStatus: "REQUESTED",
                locationUnit: selectedUnit ? { id: selectedUnit.id, name: selectedUnit.name } : { id: roomServiceId, name: roomServiceId }
            };

            const response = await fetch(`${API_URL}/api/order/service`, {
                method: "POST",
                headers: { Authorization: `Bearer ${PUBLIC_TOKEN}`, "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

            const result = await response.json();
            dispatch(addServiceRequest(result));

            // Update business roomId in Redux if not already set or different
            if (roomServiceId) {
                dispatch(setRoomId(roomServiceId));
            }

            setSubmitted(true);
        } catch (error) {
            console.error("Error submitting order:", error);
            alert(t("Failed to submit request. Please try again."));
        } finally {
            setSubmitting(false);
        }
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 flex items-center justify-center px-4">
                <div className="bg-white rounded-3xl shadow-2xl p-8 sm:p-12 text-center max-w-md border border-slate-100">
                    <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-green-200">
                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                        </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-3 font-clash">{t("Request Submitted!")}</h2>
                    <p className="text-slate-500 mb-8">{t("Your service request has been received. Our team will contact you shortly.")}</p>
                    <button onClick={() => navigate("/service-status")} className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all">
                        {t("View Status")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 pb-8">
            {/* Background Decorations */}
            <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-indigo-100/40 to-transparent rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-purple-100/30 to-transparent rounded-full blur-3xl"></div>
            </div>

            <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                {/* Back Button */}
                <button onClick={() => navigate(-1)} className="group inline-flex items-center gap-2 px-4 py-2.5 bg-white/80 backdrop-blur-sm text-indigo-600 font-medium rounded-xl hover:bg-indigo-50 border border-indigo-100/50 shadow-sm mb-6 transition-all">
                    <svg className="h-4 w-4 group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {t("Back")}
                </button>

                {/* Main Card */}
                <div className="bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden">
                    {/* Hero Image */}
                    <div className="relative h-48 sm:h-64 overflow-hidden">
                        <img src={product.serviceItemImageUrl} alt={getTranslatedName(product)} className="w-full h-full object-cover" loading="lazy" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                        <div className="absolute bottom-0 inset-x-0 p-6">
                            <h1 className="text-2xl sm:text-3xl font-bold text-white font-clash drop-shadow-lg">{getTranslatedName(product)}</h1>
                        </div>
                    </div>

                    <div className="p-5 sm:p-8 space-y-6">
                        {/* Description */}
                        <p className="text-slate-600 leading-relaxed">{getTranslatedDescription(product)}</p>

                        {/* Guest Information Section */}
                        <div className="bg-gradient-to-br from-indigo-50/50 to-purple-50/30 rounded-2xl p-5 space-y-4 border border-indigo-100/50">
                            <h3 className="text-sm font-bold text-slate-800 uppercase tracking-wide flex items-center gap-2">
                                <svg className="w-4 h-4 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                                {t("Guest Information")}
                            </h3>

                            {/* Room Service ID (Location Units) */}
                            <div className="space-y-2">
                                <label className="text-sm font-semibold text-slate-700 flex items-center gap-1">
                                    {t("Room Service ID")}
                                    <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <select
                                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all appearance-none cursor-pointer"
                                        value={roomServiceId}
                                        onChange={(e) => {
                                            const newId = e.target.value;
                                            setRoomServiceId(newId);
                                            // Immediately update Redux so bottom menu reflects the assigned room
                                            if (newId) dispatch(setRoomId(newId));
                                        }}
                                        required
                                        disabled={loadingUnits}
                                    >
                                        <option value="" disabled>{loadingUnits ? t("Loading...") : t("Select your room")}</option>
                                        {locationUnits.map((unit) => (
                                            <option key={unit.id} value={unit.id}>
                                                {unit.name || `${t("Room")} ${unit.id}`}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                        </div>

                        {/* Notes */}
                        <div className="space-y-2">
                            <label className="text-sm font-semibold text-slate-800 uppercase tracking-wide">{t("Additional Instructions")}</label>
                            <textarea className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none min-h-[100px]" placeholder={t("Any extra notes for the staff...")} value={description} onChange={(e) => setDescription(e.target.value)} />
                        </div>

                        {/* Price & Submit */}
                        <div className="border-t border-slate-100 pt-6">
                            <div className="flex justify-between items-end mb-6">
                                <span className="text-slate-500 font-medium">{t("Estimated Total")}</span>
                                <div className="text-right">
                                    <span className="block text-3xl font-bold text-slate-900 font-clash">{calculateTotal()} ALL</span>
                                </div>
                            </div>
                            <button onClick={onSubmit} disabled={submitting} className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${submitting ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-xl shadow-indigo-200 hover:shadow-2xl hover:-translate-y-0.5'}`}>
                                {submitting ? (
                                    <span className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>
                                        {t("Submitting...")}
                                    </span>
                                ) : t("Confirm Request")}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServiceRequestPage;